/**
 * Auth-Store. Hält JWT-Token + abgeleitete User-Info im RAM und
 * persistiert sie in localStorage, damit ein Page-Reload nicht zum
 * Re-Login zwingt.
 *
 * Bei Backend-401 (siehe ApiClient) räumen wir den Store via
 * handleUnauthorized auf — der globale Router-Guard schickt den User
 * dann zur Login-Page.
 */
import { defineStore } from 'pinia';
import { ref, computed } from 'vue';

const STORAGE_KEY = 'shruby.auth.v1';

interface PersistedAuth {
  token: string;
  user: string;
  roles: string[];
}

function loadFromStorage(): PersistedAuth | null {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    return JSON.parse(raw) as PersistedAuth;
  } catch {
    return null;
  }
}

export const useAuthStore = defineStore('auth', () => {
  const initial = loadFromStorage();
  const token = ref<string | null>(initial?.token ?? null);
  const user = ref<string | null>(initial?.user ?? null);
  const roles = ref<string[]>(initial?.roles ?? []);

  const isAuthenticated = computed(() => token.value !== null);
  const isAdmin = computed(() => roles.value.includes('ADMIN'));

  function setSession(t: string, u: string, r: string[]) {
    token.value = t;
    user.value = u;
    roles.value = r;
    localStorage.setItem(STORAGE_KEY, JSON.stringify({ token: t, user: u, roles: r }));
  }

  /**
   * Phase 18: Logout meldet das Token serverseitig ab (Blacklist) und
   * räumt lokalen State auf. Best-Effort beim Server-Call — wenn der
   * scheitert (Netzwerk, 5xx), trotzdem lokal aufräumen, damit der User
   * raus ist. Die WebSocket-Verbindung wird über den onLogout-Hook
   * abgeschlossen, der vom Frontend-Bootstrap registriert wird.
   */
  async function logout() {
    const hadToken = token.value !== null;
    if (hadToken) {
      // Lazy-Import, weil api/index.ts indirekt diesen Store importiert
      // (Zirkular-Risk durch Reactive-Store-Dependency)
      try {
        const { authApi } = await import('@/api');
        await authApi.logout();
      } catch {
        // Server-Logout fehlgeschlagen — lokal trotzdem aufräumen.
        // Token läuft spätestens beim TTL-Ablauf von selbst aus.
      }
    }
    token.value = null;
    user.value = null;
    roles.value = [];
    localStorage.removeItem(STORAGE_KEY);
    for (const cb of logoutListeners) cb();
  }

  // onLogout-Hook für eventStream.disconnect() o.ä.
  const logoutListeners: Array<() => void> = [];
  function onLogout(cb: () => void): () => void {
    logoutListeners.push(cb);
    return () => {
      const idx = logoutListeners.indexOf(cb);
      if (idx >= 0) logoutListeners.splice(idx, 1);
    };
  }

  /**
   * Wird vom ApiClient bei 401 gerufen. Token ist abgelaufen oder
   * gefälscht — Session aufräumen, Router-Guard übernimmt den Rest.
   */
  function handleUnauthorized() {
    logout();
  }

  return {
    token,
    user,
    roles,
    isAuthenticated,
    isAdmin,
    setSession,
    logout,
    onLogout,
    handleUnauthorized,
  };
});
